import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Alert, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Link, Stack, Avatar,
} from '@mui/material';
import {
  StyledTableContainer, TableSkeleton, FilterBar, FilterSelect, EmptyState,
} from '../components/TableShared';
import api from '../config/api';

const ff = "'Poppins', sans-serif";
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
];

const STATUS_COLORS = {
  PENDING: { bg: '#FFF3D0', fg: '#B7791F' },
  APPROVED: { bg: '#E3F0FB', fg: '#2B6CB0' },
  PROCESSING: { bg: '#E9D8FD', fg: '#6B46C1' },
  COMPLETED: { bg: '#DCF5E5', fg: '#2E9E5B' },
  REJECTED: { bg: '#FED7D7', fg: '#C53030' },
  FAILED: { bg: '#FED7D7', fg: '#C53030' },
};

export default function Withdrawals() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [dialog, setDialog] = useState(null); // { row, action }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const params = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      const res = await api.get('/admin/withdrawals', { params });
      const data = res.data.data || res.data;
      setRows(data.withdrawals || []);
      setCounts(data.counts || {});
    } catch (e) {
      setError('Failed to load withdrawals.');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const submit = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      await api.put(`/admin/withdrawals/${dialog.row.id}`, { action: dialog.action, note: note.trim() || undefined });
      setDialog(null); setNote('');
      fetchRows();
    } catch (e) {
      setError(e.response?.data?.message || 'Action failed.');
    } finally { setBusy(false); }
  };

  const docLinks = (docs) => {
    if (!docs) return null;
    const items = [
      ['ID front', docs.idFront], ['ID back', docs.idBack], ['Selfie', docs.selfie],
    ].filter(([, u]) => u);
    if (!items.length) return <Box sx={{ color: '#A0AEC0', fontSize: 11 }}>—</Box>;
    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        {items.map(([label, url]) => (
          <Link key={label} href={url} target="_blank" rel="noopener" sx={{ fontFamily: ff, fontSize: 11 }}>{label}</Link>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ fontFamily: ff }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <FilterBar>
        <FilterSelect label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={STATUS_OPTIONS} minWidth={160} />
        <Box sx={{ fontFamily: ff, fontSize: 12, color: '#718096' }}>
          {counts.PENDING ? `${counts.PENDING} pending review` : 'No pending withdrawals'}
        </Box>
      </FilterBar>

      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sitter</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Fee</TableCell>
                <TableCell>Net payout</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell align="right">Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.REJECTED;
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar src={r.user?.avatarUrl} sx={{ width: 30, height: 30 }} />
                        <Box>
                          <Box sx={{ fontWeight: 600, fontSize: 13 }}>{r.user?.fullName || 'Sitter'}</Box>
                          <Box sx={{ fontSize: 11, color: '#718096' }}>{r.user?.email}</Box>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontWeight: 600 }}>{money(r.grossCents)}</Box>
                      <Box sx={{ fontSize: 11, color: '#718096' }}>{r.amountCoins} coins</Box>
                    </TableCell>
                    <TableCell sx={{ color: '#C53030', fontSize: 13 }}>
                      –{money(r.totalFeeCents)}
                      <Box sx={{ fontSize: 10, color: '#A0AEC0' }}>{money(r.flatFeeCents)} + {money(r.percentFeeCents)}</Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#2E9E5B' }}>{money(r.netCents)}</TableCell>
                    <TableCell>{docLinks(r.documents)}</TableCell>
                    <TableCell align="right">
                      <Chip label={r.status} size="small" sx={{ fontFamily: ff, fontWeight: 600, bgcolor: sc.bg, color: sc.fg }} />
                      {r.adminNote && <Box sx={{ fontSize: 10, color: '#A0AEC0', mt: 0.5, maxWidth: 160 }}>{r.adminNote}</Box>}
                    </TableCell>
                    <TableCell align="right">
                      {r.status === 'PENDING' ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="contained" color="success" sx={{ fontFamily: ff, textTransform: 'none' }}
                            onClick={() => { setDialog({ row: r, action: 'approve' }); setNote(''); }}>Approve</Button>
                          <Button size="small" variant="outlined" color="error" sx={{ fontFamily: ff, textTransform: 'none' }}
                            onClick={() => { setDialog({ row: r, action: 'reject' }); setNote(''); }}>Reject</Button>
                        </Stack>
                      ) : (
                        <Box sx={{ fontSize: 11, color: '#A0AEC0' }}>—</Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <EmptyState colSpan={7} message="No withdrawals in this view." />}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}

      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: ff }}>
          {dialog?.action === 'approve' ? 'Approve withdrawal' : 'Reject withdrawal'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: ff, fontSize: 13, color: '#4A5568', mb: 2 }}>
            {dialog?.action === 'approve'
              ? `Approve ${money(dialog?.row?.netCents)} payout to ${dialog?.row?.user?.fullName || 'the sitter'}? They'll be able to withdraw it.`
              : `Reject this request? The ${dialog?.row?.amountCoins} held coins will be returned to the sitter.`}
          </Box>
          <TextField
            fullWidth size="small" multiline minRows={2}
            label={dialog?.action === 'reject' ? 'Reason (shown to sitter)' : 'Note (optional)'}
            value={note} onChange={(e) => setNote(e.target.value)}
            sx={{ fontFamily: ff }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy} sx={{ fontFamily: ff }}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (dialog?.action === 'reject' && !note.trim())} variant="contained"
            color={dialog?.action === 'approve' ? 'success' : 'error'} sx={{ fontFamily: ff }}>
            {busy ? 'Working…' : (dialog?.action === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
